import { formatShortDate } from 'features/libra/dashboard/ticketDashboard/libra/formatShortDate'
import type { FieldPillSettings } from 'features/libra/dashboard/ticketDashboard/libra/settings/dashboardSettings'
import type { Pill } from 'features/libra/dashboard/ticketDashboard/types'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { displayNameFromEmail } from 'utils/stringHelpers'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function toText(value: unknown): string | undefined {
  if (typeof value === 'string') return value || undefined
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return undefined
}

/** Follows a dot path through own properties only, so config can't reach inherited ones. */
function readPath(root: unknown, path: string): unknown {
  let node = root
  for (const segment of path.split('.')) {
    if (!isRecord(node) || !Object.hasOwn(node, segment)) return undefined
    node = node[segment]
  }
  return node
}

/**
 * `undefined` means nothing recorded; a multi-select gives one entry per
 * choice, and a shape that can't be shown (e.g. an object) gives an empty
 * list rather than counting as missing.
 */
function toValues(value: unknown): string[] | undefined {
  if (value === undefined || value === null || value === '') return undefined
  if (Array.isArray(value)) {
    if (value.length === 0) return undefined
    return value.flatMap((item) => toText(item) ?? [])
  }
  const text = toText(value)
  return text === undefined ? [] : [text]
}

/** A `fields` source unwraps the `FieldValue` at the end of its path into `fieldValues`. */
function readRecorded(
  field: FieldPillSettings,
  ticket: LibraTicket
): string[] | undefined {
  if (field.source === 'ticket') return toValues(readPath(ticket, field.path))
  const node = readPath(ticket.fieldValues, field.path)
  return toValues(isRecord(node) ? node.value : undefined)
}

/** A ticket property such as `terminalTransitionBy` belongs to one ticket, so it always reads per ticket. */
export function fieldPillScope(
  field: FieldPillSettings
): FieldPillSettings['scope'] {
  return field.source === 'ticket' ? 'ticket' : field.scope
}

function formatValue(value: string, format: FieldPillSettings['format']) {
  if (format === 'user') return displayNameFromEmail(value)
  if (format === 'date') return formatShortDate(value)
  return value
}

const SOURCES: Record<FieldPillSettings['scope'], string> = {
  asset:
    "From the asset's latest completed ticket, or its latest ticket if none is complete.",
  ticket: 'From this ticket.',
}

/**
 * One pill per recorded value (several for a multi-select), or the configured
 * `empty` value when nothing is recorded. The modal's details row reads the
 * value, what it means, then where it comes from.
 */
export function fieldPills(
  field: FieldPillSettings,
  ticket: LibraTicket | undefined
): Pill[] {
  const recorded = ticket && readRecorded(field, ticket)
  const values = recorded ?? (field.empty === undefined ? [] : [field.empty])
  const fieldDescription =
    field.description ??
    (recorded === undefined ? undefined : SOURCES[fieldPillScope(field)])
  return values.map((value): Pill => {
    const known = field.values.find((option) => option.value === value)
    // The `empty` text is config copy, not a recorded value, so it isn't formatted.
    const label =
      known?.label ?? (recorded ? formatValue(value, field.format) : value)
    return {
      category: field.id,
      value,
      label,
      tooltip: known?.description,
      tone: known?.tone,
      icon: known?.icon,
      info: {
        title: field.label,
        valueDescription: [label, known?.description]
          .filter(Boolean)
          .join(' · '),
        description: fieldDescription,
      },
    }
  })
}
